import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { GetAPIClients } from './useAPIClients'

const DeleteAPIClientMutation = graphql(/* GraphQL */ `
  mutation deleteAPIClient($input: DeleteAPIClientInput!) {
    deleteAPIClient(input: $input) {
      ... on DeleteAPIClientSuccess {
        clientId
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

type DeleteClientResult = { success: true } | { success: false; error: string }

type UseDeleteAPIClientResult = {
  deleteClient: (clientId: string) => Promise<DeleteClientResult>
  isLoading: boolean
}

export const useDeleteAPIClient = (): UseDeleteAPIClientResult => {
  const [mutate, { loading }] = useMutation(DeleteAPIClientMutation, {
    refetchQueries: [GetAPIClients],
  })

  const deleteClient = async (
    clientId: string,
  ): Promise<DeleteClientResult> => {
    try {
      const result = await mutate({
        variables: { input: { clientId } },
      })

      if (result.error) {
        return { success: false, error: result.error.message }
      }

      const response = result.data?.deleteAPIClient

      if (response && 'clientId' in response) {
        return { success: true }
      }

      if (response && 'message' in response) {
        return { success: false, error: response.message }
      }

      return {
        success: false,
        error: 'Failed to delete client. Please try again.',
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'

      return { success: false, error: message }
    }
  }

  return {
    deleteClient,
    isLoading: loading,
  }
}
